/**
 * Gateway Response Mapper
 * Validates Gateway /auth/me response and maps to User entity
 */
import { z } from 'zod'
import type { User, UserRole } from '@domain/entities'
import { UserStatus } from '@domain/entities'
import { createLoggerWithPrefix } from '@shared/utils/logger'

const logger = createLoggerWithPrefix('gatewayMapper')

const gatewayUserDataSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  role: z.string().default('Viewer'),
  status: z.string(),
  lastLoginAt: z.string().nullable().optional(),
  isSuperAdmin: z.boolean(),
  isDevAdmin: z.boolean().default(false),
  tenantId: z.string().nullable().optional(),
  entraObjectId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
})

type GatewayUserData = z.infer<typeof gatewayUserDataSchema>

const gatewayResponseWrapperSchema = z.object({
  success: z.boolean(),
  data: gatewayUserDataSchema,
  message: z.string().nullable().optional(),
  errors: z.array(z.unknown()).nullable().optional(),
  correlationId: z.string().nullable().optional(),
  tenantId: z.string().nullable().optional(),
  timestamp: z.string().optional(),
})

const mapStatusToEnum = (status: string): UserStatus => {
  switch (status.toLowerCase()) {
    case 'active': return UserStatus.ACTIVE
    case 'inactive': return UserStatus.INACTIVE
    case 'suspended': return UserStatus.SUSPENDED
    default:
      logger.warn(`Unknown status "${status}", defaulting to ACTIVE`)
      return UserStatus.ACTIVE
  }
}

const VALID_ROLES: UserRole[] = ['Admin', 'User', 'Viewer']

const mapRole = (role: string): UserRole => {
  const trimmed = role?.trim() as UserRole
  return VALID_ROLES.includes(trimmed) ? trimmed : 'Viewer'
}

const mapValidatedDataToUser = (userData: GatewayUserData): User => {
  return {
    id: userData.id,
    email: userData.email,
    displayName: userData.displayName,
    avatarUrl: userData.avatarUrl || undefined,
    role: mapRole(userData.role),
    status: mapStatusToEnum(userData.status),
    isSuperAdmin: userData.isSuperAdmin,
    isDevAdmin: userData.isDevAdmin,
    tenantId: userData.tenantId || undefined,
    lastLoginAt: userData.lastLoginAt || undefined,
    createdAt: userData.createdAt,
    updatedAt: userData.updatedAt || undefined,
  }
}

/**
 * Map Gateway /auth/me response to User entity
 */
export const mapGatewayResponseToUser = (response: unknown): User | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Response is null, undefined, or not an object')
    return null
  }

  const responseObj = response as Record<string, unknown>

  // Try wrapped response: { success, data: {...}, ... }
  const wrapperResult = gatewayResponseWrapperSchema.safeParse(response)
  if (wrapperResult.success) {
    return mapValidatedDataToUser(wrapperResult.data.data)
  }

  // Fallback: extract data field or treat as direct user object
  let rawUserData: unknown
  if (responseObj.data && typeof responseObj.data === 'object') {
    rawUserData = responseObj.data
  } else if ('id' in responseObj || 'email' in responseObj) {
    rawUserData = responseObj
  } else {
    logger.error('Response does not contain user data')
    return null
  }

  const dataResult = gatewayUserDataSchema.safeParse(rawUserData)
  if (!dataResult.success) {
    logger.error('Gateway user data validation failed:', dataResult.error.format())
    return null
  }

  return mapValidatedDataToUser(dataResult.data)
}
